import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import {
  TextInput,
  Panel,
  Label,
  Flex,
  FormHeading,
  Explainer,
  FormGroup,
  Divider,
  Tooltip,
  Button,
  useNotification,
  PageWrapper,
  OneHalfLayout
} from '@sopranodesign/pulse-components';
import {
  useLanguages,
  LocationsConsumer,
  useMutation
} from '@sopranodesign/pulse-hooks';
import { GroupSelect } from '@sopranodesign/pulse-groups';

import {
  Utterance,
  LanguageSelect,
  LocationSelect,
  FaqSelect,
  ToggleContainer
} from '../Components/index';
import { createFaq, syncGroupsForFaq } from '../api/index';

const initialFaq = {
  name: '',
  randomResponse: false,
  groups: [],
  location: null,
  faqToShare: null
};

const CreateFaq = () => {
  const { languages, defaultLanguage } = useLanguages();

  const { showSuccess } = useNotification();

  const [faq, setFaq] = useState({
    ...initialFaq,
    utterances: languages.reduce((utterances, language) => {
      return utterances.concat([
        {
          id: utterances.length + 1,
          language_id: language.id,
          utterance: '',
          placeholder: 'Who can I speak to about annual leave?'
        },
        {
          id: utterances.length + 2,
          language_id: language.id,
          utterance: '',
          placeholder: 'Who is in charge of annual leave?'
        },
        {
          id: utterances.length + 3,
          language_id: language.id,
          utterance: '',
          placeholder: 'Who is responsible for booking annual leave?'
        }
      ]);
    }, []),
    language: defaultLanguage.id
  });

  const handleNameChange = event =>
    setFaq({ ...faq, name: event.target.value });

  const handleUtteranceChange = (id, newUtterance) => {
    setFaq({
      ...faq,
      utterances: faq.utterances.map(utterance => {
        if (id === utterance.id) {
          return { ...utterance, utterance: newUtterance };
        }

        return utterance;
      })
    });
  };

  const handleLocationSelect = location => {
    setFaq({
      ...faq,
      location: location ? { id: location.value, name: location.label } : null
    });
  };

  const handleLanguageChange = ({ value }) =>
    setFaq({ ...faq, language: value });

  const handleGroupSelect = groups =>
    setFaq({ ...faq, groups: groups ? groups.map(group => group.value) : [] });

  const handleFaqToShare = option => {
    setFaq({ ...faq, faqToShare: option ? option.value : null });
  };

  const { mutate: syncGroups } = useMutation({
    mutationFn: syncGroupsForFaq
  });

  const { mutate: create } = useMutation({
    mutationFn: createFaq,
    onSuccess: ({ data }) => {
      // Add any FAQ groups.
      if (faq.groups.length !== 0) {
        syncGroups({ faqId: data.data.id, groupIds: faq.groups });
      }

      setFaq({ ...faq, created: data.data.id });

      showSuccess(`Successfully created "${data.data.name}"`);
    }
  });

  const handleSubmit = async e => {
    e.preventDefault();

    await create({
      name: faq.name,
      randomResponse: faq.randomResponse,
      sharedFaqId: faq.shared_faqs,
      locationId: faq.location ? faq.location?.id : null,
      utterances: faq.utterances
        ? faq.utterances.map(({ utterance, language_id }) => ({
          utterance,
          language_id
        }))
        : null
    });
  };

  const checkValid = ({ utterance }) => utterance;
  const validUtterances = faq.faqToShare || faq.utterances.every(checkValid);
  const validated = faq.name && validUtterances;

  return (
    <>
      {faq.created && (
        <Redirect
          to={{
            pathname: `/faqs/${faq.created}/responses`,
            state: { creatingFaq: true }
          }}
        />
      )}
      <PageWrapper header="FAQ" subheader="Create a new FAQ">
        <OneHalfLayout>
          <Panel>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Name</Label>
                <TextInput
                  placeholder="Book Annual Leave"
                  name="name"
                  value={faq.name}
                  onChange={handleNameChange}
                  id="name"
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="tags">Tags</Label>
                <GroupSelect
                  id="tags"
                  value={faq.groups}
                  onChange={handleGroupSelect}
                  for="faqs"
                  sortAlphabetically
                />
              </FormGroup>
              <LocationsConsumer>
                <FormGroup>
                  <Label htmlFor="location">Location</Label>
                  <LocationSelect
                    value={faq.location ? faq.location?.id : null}
                    onChange={handleLocationSelect}
                  />
                </FormGroup>
                <Flex col mb>
                  <ToggleContainer
                    onOpenChange={() => handleFaqToShare(null)}
                    initialOpen={faq.faqToShare}
                    text="Does this FAQ exist at another location?"
                    tooltip={
                      <Tooltip.Content>
                        Sharing one set of questions across multple locations
                        improves the chatbots accuracy.
                      </Tooltip.Content>
                    }>
                    <Label>Shared FAQ</Label>
                    <FaqSelect
                      value={faq.faqToShare}
                      onChange={handleFaqToShare}
                    />
                  </ToggleContainer>
                </Flex>
              </LocationsConsumer>
              {!faq.faqToShare && (
                <>
                  <Divider />
                  <Flex between>
                    <FormHeading>Questions</FormHeading>
                    <Flex center mb>
                      <Tooltip
                        tooltip={
                          <Tooltip.Content>
                            Make sure to include questions for each language
                          </Tooltip.Content>
                        }>
                        <Tooltip.Icon>!</Tooltip.Icon>
                      </Tooltip>
                      <LanguageSelect
                        value={faq.language}
                        onChange={handleLanguageChange}
                      />
                    </Flex>
                  </Flex>
                  <Explainer mb>
                    Write three variations of this question. Don&apos;t worry,
                    you can add more later on.
                  </Explainer>
                  <Flex col ySpace mb>
                    {faq.utterances
                      .filter(
                        utterance => utterance.language_id === faq.language
                      )
                      .map((utterance, key) => (
                        <Utterance
                          {...utterance}
                          key={key}
                          handleUtteranceChange={handleUtteranceChange}
                        />
                      ))}
                  </Flex>
                </>
              )}
              <Flex right xSpace>
                <Button variant="primary" disabled={!validated} type="submit">
                  Save
                </Button>
              </Flex>
            </form>
          </Panel>
        </OneHalfLayout>
      </PageWrapper>
    </>
  );
};

CreateFaq.propTypes = {
  handleSubmit: PropTypes.func.isRequired
};

export default CreateFaq;
